import { 
  Charger, 
  Session,
  QueueEntry,
  QueueDetail,
  StartChargingRequest, 
  DelaySessionRequest, 
  ChargerStatusRequest,
  GeoLocation
} from '../types';
import { apiService } from './apiService';

class ChargerService {
  async getChargers(): Promise<Charger[]> {
    const response = await apiService.get<any[]>('/chargers');
    return response.map(this.mapChargerResponse);
  }

  private mapChargerResponse(apiCharger: any): Charger {
    // Map backend status to frontend status values
    let mappedStatus: 'idle' | 'charging' | 'awaitingNext';
    const backendStatus = apiCharger.status.toLowerCase();
    
    if (backendStatus === 'awaitingnext') {
      mappedStatus = 'awaitingNext';
    } else {
      mappedStatus = backendStatus as 'idle' | 'charging' | 'awaitingNext';
    }

    return {
      id: apiCharger.id,
      slotNumber: apiCharger.slotNumber,
      name: apiCharger.name,
      online: apiCharger.isOnline,
      status: mappedStatus,
      currentSessionId: apiCharger.currentUser ? 'active' : undefined,
      currentUser: apiCharger.currentUser,
      queueUserIds: [], // API doesn't return this, would need separate endpoint
      queueLength: apiCharger.queueLength || 0,
      maxQueueLength: apiCharger.maxQueueLength || 4,
      lastChangeAt: new Date().toISOString(), // Default value
      version: 1, // Default value
      estimatedEndTime: apiCharger.estimatedEndTime,
      canJoinQueue: apiCharger.canJoinQueue || false,
      isUserInQueue: apiCharger.isUserInQueue || false,
      userQueuePosition: apiCharger.userQueuePosition,
      isUserCharging: apiCharger.isUserCharging || false,
      isUserChargingAnywhere: apiCharger.isUserChargingAnywhere || false,
      isSessionOverdue: apiCharger.isSessionOverdue || false,
      maxEndTime: apiCharger.maxEndTime
    };
  }

  async getCharger(id: string): Promise<Charger> {
    const response = await apiService.get<any>(`/chargers/${id}`);
    return this.mapChargerResponse(response);
  }

  async getChargerQueue(id: string): Promise<QueueDetail> {
    const response = await apiService.get<any>(`/chargers/${id}/queue`);
    return {
      chargerId: response.chargerId,
      chargerName: response.chargerName,
      slotNumber: response.slotNumber,
      status: response.status.toLowerCase() as 'idle' | 'charging' | 'awaitingNext',
      isOnline: response.isOnline,
      maxQueueLength: response.maxQueueLength,
      currentUser: response.currentUser,
      estimatedEndTime: response.estimatedEndTime,
      queueUsers: response.queueUsers,
      canJoinQueue: response.canJoinQueue,
      isUserInQueue: response.isUserInQueue,
      userQueuePosition: response.userQueuePosition
    };
  }

  async startCharging(chargerId: string, request: StartChargingRequest): Promise<Session> {
    return apiService.post<Session>(`/chargers/${chargerId}/start`, request);
  }

  async forceStartCharging(chargerId: string, request: StartChargingRequest): Promise<Session> {
    return apiService.post<Session>(`/chargers/${chargerId}/force-start`, request);
  }

  async stopCharging(chargerId: string): Promise<void> {
    // Use charger endpoint - it handles finding the session internally
    await apiService.post(`/chargers/${chargerId}/stop`);
  }

  async extendCharging(_chargerId: string, request: { additionalMinutes: number }): Promise<void> {
    // For extend, we need to get the session ID first since there's no charger extend endpoint
    const activeSession = await this.getCurrentSession();
    if (!activeSession) {
      throw new Error('No active session found');
    }
    await apiService.post(`/sessions/${activeSession.id}/extend`, { AdditionalMinutes: request.additionalMinutes });
  }

  async reduceCharging(_chargerId: string, request: { minutesToReduce: number }): Promise<void> {
    // For reduce, we need to get the session ID first since there's no charger reduce endpoint
    const activeSession = await this.getCurrentSession();
    if (!activeSession) {
      throw new Error('No active session found');
    }
    await apiService.post(`/sessions/${activeSession.id}/reduce`, { MinutesToReduce: request.minutesToReduce });
  }

  async getCurrentSession(): Promise<Session | null> {
    try {
      return await apiService.get<Session>('/sessions/active');
    } catch (error) {
      // Return null if no active session
      return null;
    }
  }

  async delayCharging(chargerId: string, request: DelaySessionRequest): Promise<void> {
    await apiService.post(`/chargers/${chargerId}/delay`, request);
  }

  async joinQueue(
    chargerId: string,
    shift?: number,
    access?: { location?: GeoLocation | null; captchaPassToken?: string }
  ): Promise<void> {
    await apiService.post(`/chargers/${chargerId}/queue/join`, {
      shift,
      location: access?.location ?? undefined,
      captchaPassToken: access?.captchaPassToken,
    });
  }

  async leaveQueue(chargerId: string): Promise<void> {
    console.log(`🚪 Leaving queue for charger ${chargerId}...`);
    await apiService.post(`/chargers/${chargerId}/queue/cancel`);
    console.log(`✅ Left queue for charger ${chargerId}`);
  }

  async cancelQueue(chargerId: string): Promise<void> {
    await apiService.post(`/chargers/${chargerId}/queue/cancel`);
  }

  async acceptQueue(chargerId: string): Promise<void> {
    await apiService.post(`/chargers/${chargerId}/queue/accept`);
  }

  async postponeQueue(chargerId: string): Promise<void> {
    await apiService.post(`/chargers/${chargerId}/queue/postpone`);
  }

  async updateParkingSlot(chargerId: string, parkingSlot: string | null): Promise<void> {
    await apiService.post(`/chargers/${chargerId}/queue/parking-slot`, { parkingSlot });
  }

  async getMyQueueEntries(): Promise<QueueEntry[]> {
    try {
      console.log('📡 Fetching queue entries from /chargers/queue/my-entries...');
      const response = await apiService.get<{ entries: QueueEntry[] }>('/chargers/queue/my-entries');
      console.log('✅ Queue entries received:', response);
      return response.entries || [];
    } catch (error) {
      console.error('❌ Failed to fetch queue entries:', error);
      throw error;
    }
  }

  async updateStatus(chargerId: string, request: ChargerStatusRequest): Promise<void> {
    await apiService.post(`/chargers/${chargerId}/status`, request);
  }
}

export const chargerService = new ChargerService();